import {
  type MDXEditorMethods,
  MDXEditor as OriginalMDXEditor,
  headingsPlugin,
  listsPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  linkPlugin,
  tablePlugin,
  imagePlugin,
  toolbarPlugin,
  BoldItalicUnderlineToggles,
  BlockTypeSelect,
  CreateLink,
  linkDialogPlugin,
} from '@mdxeditor/editor';
import { useField } from 'informed';
import { CircleHelp } from 'lucide-react';
import { type ClipboardEvent, useEffect, useRef } from 'react';

import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';

// Import the required CSS
import '@mdxeditor/editor/style.css';
import './styles.css';

import type { FormSchemaFieldDef } from '../../form/formUtils';

type MDXEditorProps = FormSchemaFieldDef & {
  enableMarkdownPasteTransform?: boolean;
};

const looksLikeMarkdown = (text: string) =>
  /^#{1,6}\s/m.test(text) ||
  /^\s*[*+-]\s/m.test(text) ||
  /^\s*\d+\.\s/m.test(text) ||
  /\*\*[^*]+\*\*/.test(text) ||
  /\[[^\]]+]\([^)]+\)/.test(text) ||
  /```[\S\s]*```/.test(text);

// The editor component that works with Informed and your form system
export const MDXEditor = (props: MDXEditorProps) => {
  const {
    name,
    label,
    required,
    placeholder,
    disabled,
    description,
    tooltip,
    showOptional,
    enableMarkdownPasteTransform = false,
    ...rest
  } = props;

  const editorRef = useRef<MDXEditorMethods>(null);

  // Track if the editor has been initialized with a value
  // const [hasInitialized, setHasInitialized] = useState(false);

  const { fieldState, fieldApi, render, userProps } = useField({
    name,
    // Pass initialValue and defaultValue properly to Informed
    initialValue:
      props.initialValue === undefined ? undefined : String(props.initialValue),
    defaultValue:
      props.defaultValue === undefined ? '' : String(props.defaultValue),
    ...rest,
  });

  // Update editor content when field value changes from outside
  useEffect(() => {
    if (
      editorRef.current &&
      fieldState.value !== undefined &&
      typeof fieldState.value === 'string' &&
      fieldState.value !== editorRef.current.getMarkdown()
    ) {
      editorRef.current.setMarkdown(fieldState.value);
    }
  }, [fieldState.value]);

  const handleChange = (markdown: string) => {
    fieldApi.setValue(markdown);
    fieldApi.setTouched(true);
  };

  const handleLabelClick = () => {
    if (editorRef.current && !disabled) {
      editorRef.current.focus();
    }
  };

  const handlePasteCapture = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!enableMarkdownPasteTransform || disabled || !editorRef.current) {
      return;
    }

    const pastedText = event.clipboardData.getData('text/plain');
    if (!pastedText || !looksLikeMarkdown(pastedText)) {
      return;
    }

    // Let native paste run at the current cursor position, then
    // re-parse markdown so markdown syntax is converted to rich nodes.
    window.setTimeout(() => {
      if (!editorRef.current) {
        return;
      }

      const normalizedMarkdown = editorRef.current.getMarkdown();
      fieldApi.setValue(normalizedMarkdown);
      editorRef.current.setMarkdown(normalizedMarkdown);
    }, 0);
  };

  return render(
    <div>
      {label && (
        <label
          className='mb-2 inline-flex cursor-pointer items-center gap-1 text-sm font-medium'
          htmlFor={name}
          onClick={handleLabelClick}
        >
          {label}

          {tooltip && (
            <SimpleTooltip title={tooltip}>
              <span className='cursor-help text-sm text-placeholder'>
                <CircleHelp size={14} />
              </span>
            </SimpleTooltip>
          )}

          {showOptional && (
            <span className='text-sm text-placeholder'>(optional)</span>
          )}
        </label>
      )}

      {description && (
        <p className='mb-2 text-xs text-gray-500'>{description}</p>
      )}

      <div className='rounded-md border' onPasteCapture={handlePasteCapture}>
        <OriginalMDXEditor
          ref={editorRef}
          markdown={
            typeof fieldState.value === 'string' ? fieldState.value : ''
          }
          onChange={markdown => handleChange(markdown)}
          placeholder={placeholder}
          readOnly={disabled}
          contentEditableClassName='MDXEditorContent'
          className='max-w-full'
          plugins={[
            headingsPlugin(),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            markdownShortcutPlugin(),
            linkPlugin(),
            linkDialogPlugin(),
            tablePlugin(),
            imagePlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <div className='flex flex-wrap gap-2 '>
                  <BoldItalicUnderlineToggles />
                  <CreateLink />
                  <BlockTypeSelect />
                </div>
              ),
            }),
          ]}
          {...userProps}
        />
      </div>
    </div>,
  );
};
